import { useTranslation } from "react-i18next";

import { Card } from "../../components/game/Card.js";
import type { Snapshot } from "../../services/generated/client.js";
import { sideLabel } from "../../lib/roster.js";
import type { GameView } from "../../services/gameLink.js";

/**
 * Taking a card off somebody — the power that had no way to be used.
 *
 * # It was a button that always failed
 *
 * `steal` and `challenge` are the two shop items the engine refuses without a
 * target (`Rejection::NeedsATarget`), and the shop bought everything the same
 * way. So a player with the coins tapped *Steal*, got *"that power has to say
 * what it's aimed at"*, and had no way to aim it: `game.steal` had existed since
 * phase 4 and **no screen had ever called it**. Phase 14 B2 is the finding that
 * the controls do not say what to press; this one could not be pressed at all.
 *
 * # Collapsed until it is wanted
 *
 * A grid of everybody else's cards is a lot of screen for a power most rounds
 * nobody buys, and B2's direction is one primary action per state with
 * everything else clearly secondary. So it is a single line until somebody opens
 * it, and the cards are only listed then.
 *
 * # It shows the price, not an affordability guess
 *
 * Whether it is affordable is a subtraction and it is checked here so the
 * targets are not offered for nothing — but the engine checks the purchase
 * again, and its refusal is what reaches the screen if the two ever disagree.
 */
export function StealPanel({
  board,
  roster,
  yourSide,
  price,
  coins,
  stealsUsed,
  stealsAllowed,
  busy,
  open,
  onOpenChange,
  onSteal,
}: {
  board: Snapshot;
  roster: GameView["roster"];
  /** The viewer's side, so it is not offered its own cards. */
  yourSide: string | null;
  /** `GameRules.steal`. */
  price: number;
  /** Settled coins this side can spend. */
  coins: number;
  /**
   * Steals this side has spent, and how many it gets for the game.
   *
   * **Phase 18, finding #18.** A cap a player can only discover by being
   * refused is a rule nobody knows — the same argument `voids_used` was put on
   * the view for. Optional so a read-only caller need not supply it.
   */
  stealsUsed?: number;
  /** The per-side allowance for the whole game. */
  stealsAllowed?: number;
  busy: boolean;
  /**
   * Whether the targets are showing.
   *
   * Owned by the round screen rather than here, because the *shop* opens it too:
   * tapping `steal` there used to send a purchase the engine refuses without a
   * target, and now it opens this instead.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Take this card off that side. */
  onSteal: (from: string, track: string) => void;
}) {
  const { t } = useTranslation();

  // A side with a hidden timeline has no cards in the payload to point at, which
  // is `blind_timeline` working: what it hides is *which* cards, and you cannot
  // take a card you cannot name.
  const targets = board.sides.filter(
    (side) => side.id !== yourSide && !side.eliminated && side.timeline.length > 0,
  );
  const affordable = coins >= price;

  return (
    <section aria-label={t("round.steal.title")} className="min-w-0">
      <button
        type="button"
        aria-expanded={open}
        disabled={busy || !affordable}
        onClick={() => {
          onOpenChange(!open);
        }}
        className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border border-white/15 bg-stage-900 px-3 text-left font-medium disabled:opacity-40"
      >
        <span>{open ? t("round.steal.close") : t("round.steal.open")}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-coin-400">
          {price}
          <span className="sr-only"> {t("coins.unit", { count: price })}</span>
        </span>
      </button>

      {open && (
        <div className="mt-2 flex flex-col gap-3 rounded-lg border border-white/15 p-3">
          <p className="text-xs text-white/55">{t("round.steal.lead", { price })}</p>
          {stealsAllowed !== undefined && stealsUsed !== undefined && (
            <p className="text-xs font-medium text-white/45">
              {t("round.steal.allowance", {
                left: Math.max(0, stealsAllowed - stealsUsed),
                total: stealsAllowed,
              })}
            </p>
          )}
          {targets.length === 0 ? (
            <p className="text-xs text-white/45">{t("round.steal.nobody")}</p>
          ) : (
            targets.map((side) => (
              <div key={side.id} className="min-w-0">
                <h3 className="mb-1 text-xs font-semibold text-white/60">
                  {sideLabel(roster, side.id)?.name ?? t("round.cue.somebody")}
                </h3>
                <ul className="flex flex-wrap gap-2">
                  {side.timeline.map((card) => (
                    <li key={card.track}>
                      <button
                        type="button"
                        disabled={busy}
                        aria-label={t("round.steal.take", {
                          title: card.title,
                          artist: card.artist,
                          year: card.year,
                        })}
                        data-testid="steal-target"
                        onClick={() => {
                          onSteal(side.id, card.track);
                          onOpenChange(false);
                        }}
                        className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-flash-500 disabled:opacity-40"
                      >
                        <Card
                          artist={card.artist}
                          title={card.title}
                          year={card.year}
                          compact
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}
